/**
 * The lock the Supabase auth clients use to coordinate token refreshes
 * between tabs, made safe for a browser that DENIES it.
 *
 * supabase-js picks `navigatorLock` (the Web Locks API) for a browser client
 * that persists its session. A document whose storage is denied, such as a
 * club website embedding /<club>/embed/events in an iframe while the browser
 * blocks third-party storage, refuses every `navigator.locks.request` with
 * "SecurityError: The request was denied." The client's own start-up
 * (GoTrueClient.initialize) then rejected with nobody to catch it, and Sentry
 * recorded an unhandled rejection on each of those page loads
 * (CLUB-CENTRAL-TENANT-5B, with a stack, and -4M, the same rejection from
 * another client without one).
 *
 * A browser that denies the lock has no shared storage for other tabs to race
 * over, so the work runs without it. Everything else is exactly navigatorLock:
 * a lock that is granted is used, a timeout still throws, and an error thrown
 * by the work itself is never mistaken for a refused lock.
 */
import { navigatorLock } from "@supabase/supabase-js";

const isSecurityError = (err: unknown): boolean =>
  !!err && typeof err === "object" && (err as { name?: unknown }).name === "SecurityError";

const hasWebLocks = (): boolean => {
  try {
    return typeof globalThis.navigator?.locks?.request === "function";
  } catch {
    return false;
  }
};

export async function storageSafeLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> {
  if (!hasWebLocks()) return fn();
  let entered = false;
  try {
    return await navigatorLock(name, acquireTimeout, () => {
      entered = true;
      return fn();
    });
  } catch (err) {
    // Refused before the work started: run it unlocked. Once the work has
    // started, its own errors are its caller's to handle.
    if (!entered && isSecurityError(err)) return fn();
    throw err;
  }
}
